import Rollup from './Rollup';
import onFocusedOtherRollupClicked from '../utils/onFocusedOtherRollupClicked';
import shouldShowFocusedRollup from '../utils/shouldShowFocusedRollup';
import { observer } from 'mobx-react-lite';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import * as React from 'react';
import { lazyGetSpotlightRollupData, lazyOnSpotlightRollupDismissed } from 'owa-mail-spotlight';
import {
    focusedInboxRollupStore,
    getRollupText,
    updateWatermarkAndResetRollup,
} from 'owa-mail-focused-inbox-rollup-store';
import { lazyLoadSpotlightFilter } from 'owa-mail-filter-actions';

const styles = require('./RollupContainer.scss');

export interface RollupContainerProps {
    previousRowKey: string;
    currentRowKey: string;
    folderId: string;
    focusedViewFilter: FocusedViewFilter;
    styleSelectorAsPerUserSettings: string;
}

export default observer(function RollupContainer(props: RollupContainerProps) {
    const {
        previousRowKey,
        currentRowKey,
        folderId,
        focusedViewFilter,
        styleSelectorAsPerUserSettings,
    } = props;

    // If spotlightRollupData is non-null, then it means we should show it.
    const getSpotlightRollupData = lazyGetSpotlightRollupData.tryImportForRender();
    const spotlightRollupData = getSpotlightRollupData?.(previousRowKey, folderId);

    // Only evaluate if we determine either F/O rollup should be shown.
    let showFocusedRollup = false;
    let showOtherRollup = false;
    if (shouldShowFocusedRollup(previousRowKey, currentRowKey, folderId)) {
        // Show if we're in Other pivot.
        showFocusedRollup = focusedViewFilter == FocusedViewFilter.Other;

        // Show if we're in Focused pivot and there's no spotlight rollup.
        showOtherRollup = focusedViewFilter == FocusedViewFilter.Focused && !spotlightRollupData;
    }

    /**
     * Tracks if spotlight rollup is shown. Should only be shown when there
     * is spotlight data to show and the focused rollup is not shown.
     */
    const showSpotlightRollup = spotlightRollupData && !showFocusedRollup;

    const onFocusedOtherRollupDismissed = React.useCallback(() => {
        updateWatermarkAndResetRollup(focusedViewFilter, 'ExitRollupButton');
    }, [focusedViewFilter]);

    const onSpotlightRollupClicked = React.useCallback(() => {
        lazyLoadSpotlightFilter.importAndExecute('SpotlightRollup');
    }, []);

    const onSpotlightRollupDismissed = React.useCallback(() => {
        lazyOnSpotlightRollupDismissed.importAndExecute();
    }, []);

    if (showFocusedRollup || showOtherRollup) {
        return (
            <Rollup
                onClick={onFocusedOtherRollupClicked}
                onDismissed={onFocusedOtherRollupDismissed}
                primaryText={getRollupText(focusedInboxRollupStore.viewType, folderId)}
                secondaryText={focusedInboxRollupStore.uniqueSenders}
                styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                styles={{
                    containerClassName: styles.focusedOtherRollupContainer,
                    dismissButtonClassName: styles.focusedOtherDismissButton,
                    primaryTextClassName: styles.focusedOtherPrimaryText,
                    secondaryTextClassName: styles.focusedOtherSecondaryText,
                }}
            />
        );
    }

    if (showSpotlightRollup) {
        const { unseenCount, senderNames } = spotlightRollupData;

        return (
            <Rollup
                badgeCount={unseenCount}
                onClick={onSpotlightRollupClicked}
                onDismissed={onSpotlightRollupDismissed}
                // Work Item 77854: Add localized strings (when strings are finalized)
                primaryText="New important messages"
                secondaryText={senderNames}
                showOverlay={true}
                styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                styles={{
                    primaryTextClassName: styles.spotlightPrimaryText,
                    secondaryTextClassName: styles.spotlightSecondaryText,
                    dismissButtonIconClassName: styles.spotlightDismissButtonIcon,
                }}
            />
        );
    }

    return null;
});
